import { Css, maybeTooltip, SelectField, Tokens, useTestIds } from "@homebound/beam";
import { Fragment, useState } from "react";
import { FileField } from "src/components/FileField";
import { useUppyUploader } from "src/components/uploader/UppyUploader";
import { AssetInput, DocumentType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { useConstructionDocumentPickerDocumentsQuery } from "src/routes/libraries/plan-package-v2/components/ConstructionDocumentPicker.generated";
import { PlanPackageDetailsParams } from "src/routes/routesDef";

type Source = "existing" | "upload";

export type ConstructionDocumentPickerState = ReturnType<typeof useConstructionDocumentPicker>;

/**
 * Holds the construction document a Blueprint AI modal starts from: one of the plan's Plan documents on this version,
 * or a new file. `submit` turns the choice into an `AssetInput`, uploading a new file first, and hands it to `onAsset`.
 */
export function useConstructionDocumentPicker(onAsset: (asset: AssetInput) => Promise<unknown>) {
  const { planPackageId, planPackageVersionId } = useRouteParams<PlanPackageDetailsParams>();
  const [selectedSource, setSource] = useState<Source>();
  const [selectedDocumentId, setDocumentId] = useState<string>();
  const [file, setFile] = useState<File>();
  const [submitting, setSubmitting] = useState(false);
  const { data, loading } = useConstructionDocumentPickerDocumentsQuery({
    variables: {
      filter: { readyPlanId: planPackageId, versionId: planPackageVersionId, documentType: [DocumentType.Plan] },
      page: {},
    },
  });
  const uppy = useUppyUploader({
    autoProceed: true,
    maxNumberOfFiles: 1,
    onFinish: (uppyFile) =>
      void handleAsset({
        contentType: uppyFile.type,
        fileName: uppyFile.name,
        s3Key: uppyFile.meta.s3Key,
        fileSizeInBytes: uppyFile.size ?? 0,
      }),
    onError: () => setSubmitting(false),
  });

  const documents = data?.documents.entities ?? [];
  // With no Plan documents there's nothing to use, so start on Upload New
  const source = selectedSource ?? (loading || documents.nonEmpty ? "existing" : "upload");
  const documentId = selectedDocumentId ?? (documents.length === 1 ? documents[0].id : undefined);
  const document = documents.find((d) => d.id === documentId);
  const missing =
    source === "existing" ? !document && "Select a construction document" : !file && "Upload a construction document";

  async function handleAsset(asset: AssetInput) {
    try {
      await onAsset(asset);
    } catch {
      // The mutation reports its own failure via the Apollo error snackbar, so just leave the modal
      // open on the chosen document and let the user try again.
    } finally {
      setSubmitting(false);
    }
  }

  function submit() {
    if (source === "existing" && document) {
      setSubmitting(true);
      void handleAsset({ id: document.asset.id });
    } else if (source === "upload" && file) {
      setSubmitting(true);
      // Uppy only clears itself after a fully successful run, so drop any failed prior attempt
      // first — otherwise `addFile` rejects the retry as a duplicate / over `maxNumberOfFiles`.
      uppy.clear();
      uppy.addFile({ name: file.name, size: file.size, type: file.type, data: file });
    }
  }

  return {
    loading,
    documents,
    source,
    setSource,
    documentId,
    setDocumentId,
    file,
    setFile,
    missing,
    submitting,
    submit,
  };
}

type ConstructionDocumentPickerProps = { picker: ConstructionDocumentPickerState };

/** Picks between the plan's existing construction documents and uploading a new one; state lives in `picker`. */
export function ConstructionDocumentPicker(props: ConstructionDocumentPickerProps) {
  const { picker } = props;
  const { loading, documents, source, setSource, documentId, setDocumentId, file, setFile } = picker;

  return (
    <div css={Css.df.fdc.gap1.$}>
      <h3 css={Css.lg.color(Tokens.OnSurface).$}>Construction Document</h3>
      <SourceToggle source={source} onChange={setSource} loading={loading} hasDocuments={documents.nonEmpty} />
      <div css={Css.pt(0.5).$}>
        {source === "existing" ? (
          <SelectField
            label="Documents"
            fullWidth
            disabled={loading}
            options={documents}
            value={documentId}
            onSelect={setDocumentId}
            getOptionLabel={(d) => d.name}
            getOptionValue={(d) => d.id}
          />
        ) : (
          <FileField label="Drag & drop" file={file} setFile={setFile} />
        )}
      </div>
    </div>
  );
}

type SourceToggleProps = {
  source: Source;
  onChange: (source: Source) => void;
  loading: boolean;
  hasDocuments: boolean;
};

/** The design's full-width Use Existing / Upload New switch, which Beam's content-sized `ButtonGroup` can't draw. */
function SourceToggle(props: SourceToggleProps) {
  const { source, onChange, loading, hasDocuments } = props;
  const tid = useTestIds({});
  const segments = [
    {
      value: "existing" as const,
      label: "Use Existing",
      testId: tid.useExisting,
      disabled: loading || !hasDocuments,
      tooltip: !loading && !hasDocuments ? "No construction documents on this version" : undefined,
    },
    { value: "upload" as const, label: "Upload New", testId: tid.uploadNew, disabled: loading, tooltip: undefined },
  ];

  return (
    <div css={Css.df.$}>
      {segments.map(({ value, label, testId, disabled, tooltip }, i) => {
        const selected = source === value;
        return (
          <Fragment key={value}>
            {maybeTooltip({
              title: tooltip,
              children: (
                <button
                  type="button"
                  aria-pressed={selected}
                  disabled={disabled}
                  onClick={() => onChange(value)}
                  css={
                    Css.fg1
                      .hPx(42)
                      .ba.bcGray300.bgWhite.smSb.color(Tokens.OnSurface)
                      .cursorPointer.add("borderRadius", i === 0 ? "8px 0 0 8px" : "0 8px 8px 0")
                      // Overlap the shared edge so the two borders read as one line
                      .if(i > 0)
                      .mlPx(-1)
                      .if(selected)
                      .relative.z1.bgBlue50.bc(Tokens.Primary)
                      .if(disabled)
                      .color(Tokens.FieldTextDisabled).cursorNotAllowed.$
                  }
                  {...testId}
                >
                  {label}
                </button>
              ),
            })}
          </Fragment>
        );
      })}
    </div>
  );
}
